import type { MomContent, MomPayload } from '@sl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, momApi } from '../lib/api.js';
import {
  ActionsEditor,
  CitedListEditor,
  QuotesEditor,
  StringListEditor,
  TextAreaField,
  TextField,
} from './MomEditorFields.js';

/**
 * The review desk for the minutes: notes to clear at the top, the structured
 * editor on one side, the document exactly as it will print on the other.
 * Everything a reviewer must do before a MOM leaves the house happens here.
 */

type Lang = 'en' | 'ar';
type Mom = NonNullable<MomPayload['mom']>;

const POLLING_STATUSES = new Set(['DRAFTING', 'TRANSCRIBING']);

export function MomPanel({
  meetingId,
  canWrite,
  meetingStatus,
  onCite,
  onConfirmPeople,
  onMomLoaded,
}: {
  meetingId: string;
  canWrite: boolean;
  meetingStatus: string;
  onCite: (segmentIdx: number) => void;
  /** Opens the meeting page's "Confirm people" dialog. */
  onConfirmPeople: () => void;
  /** Tells the page whether there are minutes, so it can pick the first tab. */
  onMomLoaded: (hasMom: boolean) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [lang, setLang] = useState<Lang>(i18n.language === 'ar' ? 'ar' : 'en');
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [draft, setDraft] = useState<MomContent | null>(null);
  const [numberKey, setNumberKey] = useState('');
  const [dirty, setDirty] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [instruction, setInstruction] = useState('');
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [confirmFinalize, setConfirmFinalize] = useState(false);

  const drafting = POLLING_STATUSES.has(meetingStatus);

  const momQuery = useQuery({
    queryKey: ['mom', meetingId, selectedVersion],
    queryFn: () =>
      selectedVersion === null
        ? momApi.get(meetingId)
        : momApi.getVersion(meetingId, selectedVersion).then((r) => ({
            mom: r.mom,
            versions: r.versions,
            meetingStatus,
          })),
    refetchInterval: drafting ? 6000 : false,
  });

  const mom = momQuery.data?.mom ?? null;
  const versions = momQuery.data?.versions ?? [];
  const locked = !canWrite || mom?.status === 'FINAL';

  // The page opens on Minutes when there are minutes; it cannot know that
  // before this query lands.
  const hasContent = Boolean(mom?.contentEn ?? mom?.contentAr);
  useEffect(() => {
    if (!momQuery.isLoading) onMomLoaded(hasContent);
  }, [hasContent, momQuery.isLoading, onMomLoaded]);

  // The editable copy is seeded from the server and then owned by the form;
  // re-seeding it on every refetch would throw away what the reviewer typed.
  const serverContent = lang === 'ar' ? (mom?.contentAr ?? null) : (mom?.contentEn ?? null);
  const signature = `${mom?.id ?? ''}:${lang}`;
  const [seededFrom, setSeededFrom] = useState('');
  useEffect(() => {
    if (!serverContent) {
      if (seededFrom !== signature) {
        setDraft(null);
        setSeededFrom(signature);
      }
      return;
    }
    if (seededFrom !== signature || (!dirty && draft === null)) {
      setDraft(structuredClone(serverContent));
      setNumberKey(mom?.numberKey ?? '');
      setDirty(false);
      setSeededFrom(signature);
    }
  }, [serverContent, signature, seededFrom, dirty, draft, mom?.numberKey]);

  const patch = (next: Partial<MomContent>): void => {
    setDraft((current) => (current ? { ...current, ...next } : current));
    setDirty(true);
  };

  const save = useMutation({
    mutationFn: () => {
      if (!mom || !draft) throw new Error('nothing to save');
      return momApi.update(meetingId, mom.version, {
        ...(lang === 'ar' ? { contentAr: draft } : { contentEn: draft }),
        ...(numberKey && numberKey !== mom.numberKey ? { numberKey } : {}),
      });
    },
    onSuccess: (data) => {
      setDirty(false);
      setNotice(
        data.mom.version !== mom?.version
          ? (t('mom.forked', { version: data.mom.version }) as string)
          : (t('mom.saved') as string),
      );
      setSelectedVersion(data.mom.version === versions[0]?.version ? null : data.mom.version);
      setSeededFrom('');
      setPreviewKey((k) => k + 1);
      void queryClient.invalidateQueries({ queryKey: ['mom', meetingId] });
    },
  });

  const generate = useMutation({
    mutationFn: (note: string) =>
      momApi.generate(meetingId, note.trim() ? { instruction: note.trim() } : {}),
    onSuccess: () => {
      setShowRegenerate(false);
      setInstruction('');
      setNotice(t('mom.generating') as string);
      void queryClient.invalidateQueries({ queryKey: ['meeting', meetingId] });
      void queryClient.invalidateQueries({ queryKey: ['mom', meetingId] });
    },
    onError: (err) =>
      setNotice(
        err instanceof ApiError && err.status === 503
          ? (t('mom.noKey') as string)
          : (t('common.error') as string),
      ),
  });

  const finalize = useMutation({
    mutationFn: () => momApi.finalize(meetingId, mom!.version),
    onSuccess: (data) => {
      setConfirmFinalize(false);
      setNotice(t('mom.finalized', { docNumber: data.docNumber, n: data.actionItems }) as string);
      void queryClient.invalidateQueries({ queryKey: ['meeting', meetingId] });
      void queryClient.invalidateQueries({ queryKey: ['mom', meetingId] });
    },
    onError: () => setConfirmFinalize(false),
  });

  const rerender = useMutation({
    mutationFn: () => momApi.rerender(meetingId, mom!.version),
    onSuccess: () => setNotice(t('mom.rendering') as string),
  });

  const previewQuery = useQuery({
    queryKey: ['mom-html', meetingId, mom?.version, lang, previewKey],
    queryFn: () => momApi.html(meetingId, mom!.version, lang),
    enabled: Boolean(mom?.version),
  });

  const proposedDocNumber = mom?.docNumber ?? `MN-${numberKey || '—'}-NN`;

  if (momQuery.isLoading) return <p className="muted">{t('common.loading')}</p>;

  return (
    <section className="mom">
      <div className="spread">
        <h2 style={{ marginBottom: 0 }}>{t('mom.title')}</h2>
        <div className="row">
          <div className="mom-lang-toggle" role="group" aria-label={t('mom.language') as string}>
            <button
              type="button"
              className={lang === 'en' ? 'active' : ''}
              onClick={() => setLang('en')}
            >
              EN
            </button>
            <button
              type="button"
              className={lang === 'ar' ? 'active' : ''}
              onClick={() => setLang('ar')}
            >
              AR
            </button>
          </div>
        </div>
      </div>

      {notice && <div className="banner">{notice}</div>}
      {drafting && <div className="banner">{t('mom.drafting')}</div>}
      {mom?.error && (
        <div className="banner error">
          <strong>{t('mom.failed')}</strong>
          <div className="muted">{mom.error}</div>
        </div>
      )}

      {!mom && !drafting && (
        <div className="card">
          <p className="muted">{t('mom.none')}</p>
          {canWrite && (
            <button
              type="button"
              className="gold"
              disabled={generate.isPending}
              onClick={() => generate.mutate('')}
            >
              {t('mom.generate')}
            </button>
          )}
        </div>
      )}

      {mom && (
        <>
          <p className="muted">
            {t(`momStatus.${mom.status}`)}
            {mom.docNumber ? ` · ${mom.docNumber}` : ''}
            {` · v${mom.version}`}
            {mom.model ? ` · ${mom.model}` : ''}
            {mom.costUsd !== null ? ` · ${t('mom.cost')}: $${mom.costUsd.toFixed(3)}` : ''}
            {mom.usage
              ? ` · ${t('mom.tokens', {
                  input: mom.usage.inputTokens + mom.usage.cacheReadTokens,
                  output: mom.usage.outputTokens,
                })}`
              : ''}
          </p>

          {mom.reviewerNotes.length > 0 && (
            <div className="card mom-notes">
              <div className="spread">
                <h3 style={{ marginBottom: 0 }}>{t('mom.reviewerNotes')}</h3>
                {canWrite && (
                  <button type="button" className="secondary" onClick={onConfirmPeople}>
                    {t('people.open')}
                  </button>
                )}
              </div>
              {/* Most of these notes are "who is this person?", and the answer
                  is a dialog away. Saying what the next run costs makes the
                  decision to rerun an informed one rather than a flinch. */}
              <p className="muted small">
                {t('mom.confirmPeopleHint')}
                {mom.costUsd !== null
                  ? ` ${t('mom.regenerateCost', { cost: mom.costUsd.toFixed(2) })}`
                  : ''}
              </p>
              <ul>
                {mom.reviewerNotes.map((note, i) => (
                  <li key={i} dir="auto">
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="row mom-actions">
            {canWrite && (
              <button
                type="button"
                className="gold"
                disabled={!dirty || save.isPending || !draft}
                onClick={() => save.mutate()}
              >
                {t('common.save')}
              </button>
            )}
            {canWrite && (
              <button type="button" className="secondary" onClick={() => setShowRegenerate(true)}>
                {t('mom.regenerate')}
              </button>
            )}
            {canWrite && mom.status !== 'FINAL' && mom.contentEn && (
              <button type="button" className="secondary" onClick={() => setConfirmFinalize(true)}>
                {t('mom.finalize')}
              </button>
            )}
            {canWrite && (
              <button
                type="button"
                className="secondary"
                disabled={rerender.isPending || !mom.contentEn}
                onClick={() => rerender.mutate()}
              >
                {t('mom.rerender')}
              </button>
            )}
            {mom.hasPdfEn && (
              <a className="button secondary" href={momApi.pdfUrl(meetingId, mom.version, 'en')}>
                {t('mom.downloadEn')}
              </a>
            )}
            {mom.hasPdfAr && (
              <a className="button secondary" href={momApi.pdfUrl(meetingId, mom.version, 'ar')}>
                {t('mom.downloadAr')}
              </a>
            )}
          </div>

          {versions.length > 1 && (
            <div className="field" style={{ maxWidth: 320 }}>
              <label htmlFor="momVersion">{t('mom.version')}</label>
              <select
                id="momVersion"
                value={selectedVersion ?? versions[0]!.version}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setSeededFrom('');
                  setDirty(false);
                  setSelectedVersion(v === versions[0]!.version ? null : v);
                }}
              >
                {versions.map((v) => (
                  <option key={v.version} value={v.version}>
                    {t('mom.versionOption', {
                      version: v.version,
                      status: t(`momStatus.${v.status}`),
                      doc: v.docNumber ?? '—',
                    })}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="mom-split">
            <div className="mom-editor">
              {!draft && (
                <p className="muted">
                  {lang === 'ar' ? t('mom.noArabic') : t('mom.noContent')}
                </p>
              )}
              {draft && (
                <MomEditor
                  content={draft}
                  numberKey={numberKey}
                  disabled={locked}
                  onNumberKey={(v) => {
                    setNumberKey(v.toUpperCase());
                    setDirty(true);
                  }}
                  onChange={patch}
                  onCite={onCite}
                />
              )}
            </div>
            <div className="mom-preview">
              <div className="mom-preview-head">
                <span className="muted small">{t('mom.preview')}</span>
                <span className="muted small">{proposedDocNumber}</span>
              </div>
              {previewQuery.data ? (
                <iframe
                  title={t('mom.preview') as string}
                  srcDoc={previewQuery.data}
                  // The document is pure markup; nothing in it needs to run.
                  sandbox=""
                  className="mom-preview-frame"
                />
              ) : (
                <div className="mom-preview-frame mom-preview-empty">
                  <span className="muted small">
                    {previewQuery.isError ? t('common.error') : t('common.loading')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showRegenerate && (
        <div className="dialog-backdrop" onClick={() => setShowRegenerate(false)} role="presentation">
          <div className="dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2>{t('mom.regenerate')}</h2>
            <p className="muted">{t('mom.regenerateHint')}</p>
            {mom?.costUsd != null && (
              <p className="muted small">{t('mom.regenerateCost', { cost: mom.costUsd.toFixed(2) })}</p>
            )}
            <div className="field">
              <label htmlFor="momInstruction">{t('mom.instruction')}</label>
              <textarea
                id="momInstruction"
                dir="auto"
                rows={3}
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
              />
            </div>
            <div className="row">
              <button
                type="button"
                className="gold"
                disabled={generate.isPending}
                onClick={() => generate.mutate(instruction)}
              >
                {t('mom.generate')}
              </button>
              <button type="button" className="secondary" onClick={() => setShowRegenerate(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmFinalize && mom && (
        <div className="dialog-backdrop" onClick={() => setConfirmFinalize(false)} role="presentation">
          <div className="dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2>{t('mom.finalize')}</h2>
            <p>{t('mom.finalizeConfirm', { docNumber: `MN-${numberKey || '—'}-NN` })}</p>
            {dirty && <div className="banner">{t('mom.unsavedWarning')}</div>}
            <div className="row">
              <button
                type="button"
                className="gold"
                disabled={finalize.isPending}
                onClick={() => finalize.mutate()}
              >
                {t('mom.finalize')}
              </button>
              <button type="button" className="secondary" onClick={() => setConfirmFinalize(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function MomEditor({
  content,
  numberKey,
  disabled,
  onNumberKey,
  onChange,
  onCite,
}: {
  content: MomContent;
  numberKey: string;
  disabled: boolean;
  onNumberKey: (value: string) => void;
  onChange: (patch: Partial<MomContent>) => void;
  onCite: (idx: number) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const S = (key: string): string => t(`mom.sections.${key}`) as string;
  const F = (key: string): string => t(`mom.fields.${key}`) as string;

  return (
    <div className="mom-sections">
      <details open className="mom-section">
        <summary>{S('metadata')}</summary>
        <TextField
          label={F('numberKey')}
          value={numberKey}
          disabled={disabled}
          onChange={onNumberKey}
        />
        {(
          [
            ['mnNumber', 'mnNumber'],
            ['date', 'date'],
            ['timeAndDuration', 'timeAndDuration'],
            ['channelAndLanguage', 'channelAndLanguage'],
            ['client', 'client'],
            ['clientOrLeadId', 'clientOrLeadId'],
            ['dealId', 'dealId'],
            ['meetingType', 'meetingType'],
          ] as const
        ).map(([key, label]) => (
          <TextField
            key={key}
            label={F(label)}
            value={content.metadata[key]}
            disabled={disabled}
            onChange={(v) => onChange({ metadata: { ...content.metadata, [key]: v } })}
          />
        ))}
      </details>

      <details className="mom-section">
        <summary>{S('attendees')}</summary>
        <StringListEditor
          label={F('clientSide')}
          items={content.attendees.clientSide}
          disabled={disabled}
          onChange={(v) => onChange({ attendees: { ...content.attendees, clientSide: v } })}
        />
        <StringListEditor
          label={F('houseSide')}
          items={content.attendees.houseSide}
          disabled={disabled}
          onChange={(v) => onChange({ attendees: { ...content.attendees, houseSide: v } })}
        />
        <StringListEditor
          label={F('alsoPresent')}
          items={content.attendees.alsoPresent}
          disabled={disabled}
          onChange={(v) => onChange({ attendees: { ...content.attendees, alsoPresent: v } })}
        />
        <TextField
          label={F('hostedBy')}
          value={content.attendees.hostedBy}
          disabled={disabled}
          onChange={(v) => onChange({ attendees: { ...content.attendees, hostedBy: v } })}
        />
        <TextField
          label={F('silentOrInaudible')}
          value={content.attendees.silentOrInaudible}
          disabled={disabled}
          onChange={(v) => onChange({ attendees: { ...content.attendees, silentOrInaudible: v } })}
        />
        <TextField
          label={F('preparedBy')}
          value={content.preparedBy}
          disabled={disabled}
          onChange={(v) => onChange({ preparedBy: v })}
        />
        <TextField
          label={F('source')}
          value={content.source}
          disabled={disabled}
          onChange={(v) => onChange({ source: v })}
        />
      </details>

      <details open className="mom-section">
        <summary>{S('summary')}</summary>
        <TextAreaField
          label={S('summary')}
          rows={6}
          value={content.summary}
          disabled={disabled}
          onChange={(v) => onChange({ summary: v })}
        />
      </details>

      {content.clientAndOccasion && (
        <details className="mom-section">
          <summary>{S('clientAndOccasion')}</summary>
          {(
            [
              'occasion',
              'eventDate',
              'wantedBy',
              'budgetSignal',
              'pieceUnderDiscussion',
              'howSheReachedUs',
            ] as const
          ).map((key) => (
            <TextAreaField
              key={key}
              rows={2}
              label={F(key)}
              value={content.clientAndOccasion![key]}
              disabled={disabled}
              onChange={(v) =>
                onChange({ clientAndOccasion: { ...content.clientAndOccasion!, [key]: v } })
              }
            />
          ))}
        </details>
      )}

      {content.designBrief && (
        <details open className="mom-section">
          <summary>{S('designBrief')}</summary>
          <StringListEditor
            label={F('wants')}
            items={content.designBrief.wants}
            disabled={disabled}
            onChange={(v) => onChange({ designBrief: { ...content.designBrief!, wants: v } })}
          />
          <StringListEditor
            label={F('doesNotWant')}
            items={content.designBrief.doesNotWant}
            disabled={disabled}
            onChange={(v) => onChange({ designBrief: { ...content.designBrief!, doesNotWant: v } })}
          />
          <StringListEditor
            label={F('referencesAndTasteSignals')}
            items={content.designBrief.referencesAndTasteSignals}
            disabled={disabled}
            onChange={(v) =>
              onChange({ designBrief: { ...content.designBrief!, referencesAndTasteSignals: v } })
            }
          />
          <StringListEditor
            label={F('imagePlaceholders')}
            items={content.imagePlaceholders}
            disabled={disabled}
            onChange={(v) => onChange({ imagePlaceholders: v })}
          />
        </details>
      )}

      {content.discussionPoints.length > 0 && (
        <details className="mom-section">
          <summary>{S('discussionPoints')}</summary>
          <CitedListEditor
            label={S('discussionPoints')}
            items={content.discussionPoints}
            disabled={disabled}
            onChange={(v) => onChange({ discussionPoints: v })}
            onCite={onCite}
          />
        </details>
      )}

      {content.perDepartment.length > 0 && (
        <details className="mom-section">
          <summary>{S('perDepartment')}</summary>
          {content.perDepartment.map((row, i) => (
            <div className="mom-dept-row" key={i}>
              <TextField
                label={F('department')}
                value={row.department}
                disabled={disabled}
                onChange={(v) =>
                  onChange({
                    perDepartment: content.perDepartment.map((r, j) =>
                      j === i ? { ...r, department: v } : r,
                    ),
                  })
                }
              />
              <TextAreaField
                label={F('impactFromThisMeeting')}
                rows={3}
                value={row.impactFromThisMeeting}
                disabled={disabled}
                onChange={(v) =>
                  onChange({
                    perDepartment: content.perDepartment.map((r, j) =>
                      j === i ? { ...r, impactFromThisMeeting: v } : r,
                    ),
                  })
                }
              />
              <TextAreaField
                label={F('toConfirmBeforeQuoting')}
                rows={3}
                value={row.toConfirmBeforeQuoting}
                disabled={disabled}
                onChange={(v) =>
                  onChange({
                    perDepartment: content.perDepartment.map((r, j) =>
                      j === i ? { ...r, toConfirmBeforeQuoting: v } : r,
                    ),
                  })
                }
              />
            </div>
          ))}
        </details>
      )}

      <details open className="mom-section">
        <summary>{S('decisions')}</summary>
        <CitedListEditor
          label={S('decisions')}
          items={content.decisions}
          disabled={disabled}
          onChange={(v) => onChange({ decisions: v })}
          onCite={onCite}
        />
        <CitedListEditor
          label={S('commitmentsToClient')}
          items={content.commitmentsToClient}
          disabled={disabled}
          onChange={(v) => onChange({ commitmentsToClient: v })}
          onCite={onCite}
        />
      </details>

      <details open className="mom-section">
        <summary>{S('actions')}</summary>
        <ActionsEditor
          items={content.actions}
          disabled={disabled}
          onChange={(v) => onChange({ actions: v })}
          onCite={onCite}
        />
      </details>

      <details className="mom-section">
        <summary>{S('openQuestions')}</summary>
        <CitedListEditor
          label={S('openQuestions')}
          items={content.openQuestions}
          disabled={disabled}
          onChange={(v) => onChange({ openQuestions: v })}
          onCite={onCite}
        />
        <TextAreaField
          label={S('nextSteps')}
          rows={4}
          value={content.nextSteps}
          disabled={disabled}
          onChange={(v) => onChange({ nextSteps: v })}
        />
      </details>

      <details className="mom-section">
        <summary>{S('inTheClientsWords')}</summary>
        <QuotesEditor
          items={content.inTheClientsWords}
          disabled={disabled}
          onChange={(v) => onChange({ inTheClientsWords: v })}
          onCite={onCite}
        />
        <label className="mom-inline-check">
          <input
            type="checkbox"
            checked={content.acknowledgement}
            disabled={disabled}
            onChange={(e) => onChange({ acknowledgement: e.target.checked })}
          />
          {t('mom.fields.acknowledgement')}
        </label>
      </details>
    </div>
  );
}
